import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { Activity, Check, ChevronDown, ChevronLeft, ChevronRight, CircleAlert, HelpCircle, Info, LoaderCircle, MoreHorizontal, X } from 'lucide-react';

export function UIProvider({ children }) {
  return <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={120}>{children}</TooltipPrimitive.Provider>;
}

export function Tooltip({ children, content, side = 'top', shortcut, disabled = false }) {
  if (!content || disabled) return children;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="cc-tooltip" side={side} sideOffset={8} collisionPadding={12}>
          <span>{content}</span>{shortcut && <kbd>{shortcut}</kbd>}
          <TooltipPrimitive.Arrow className="cc-tooltip-arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const Button = forwardRef(function Button({ variant = 'primary', size = 'md', loading, icon: Icon, children, className = '', ...props }, ref) {
  return (
    <button ref={ref} className={`cc-button cc-button--${variant} cc-button--${size} ${className}`} disabled={loading || props.disabled} {...props}>
      {loading ? <LoaderCircle className="cc-spin" size={16} aria-hidden="true" /> : Icon ? <Icon size={16} aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

export const IconButton = forwardRef(function IconButton({ label, tooltip = label, shortcut, icon: Icon, children, variant = 'ghost', size = 'md', className = '', ...props }, ref) {
  return (
    <Tooltip content={tooltip} shortcut={shortcut}>
      <button ref={ref} type="button" className={`cc-icon-button cc-icon-button--${variant} cc-icon-button--${size} ${className}`} aria-label={label} {...props}>
        {Icon ? <Icon size={18} aria-hidden="true" /> : children}
      </button>
    </Tooltip>
  );
});

export function Card({ children, className = '', interactive = false, ...props }) {
  return <section className={`cc-card ${interactive ? 'cc-card--interactive' : ''} ${className}`} {...props}>{children}</section>;
}

export function StatCard({ label, value, icon: Icon, tone = 'red', hint, trend }) {
  return (
    <Card className={`cc-stat cc-stat--${tone}`}>
      <div className="cc-stat__top"><span>{label}</span>{Icon && <Icon size={18} aria-hidden="true" />}</div>
      <strong>{value}</strong>
      {(hint || trend) && <div className="cc-stat__meta">{trend && <Badge tone={trend.startsWith('+') ? 'success' : 'neutral'}>{trend}</Badge>}{hint}</div>}
    </Card>
  );
}

export function Badge({ children, tone = 'neutral', dot = false, className = '' }) {
  return <span className={`cc-badge cc-badge--${tone} ${className}`}>{dot && <i aria-hidden="true" />}{children}</span>;
}

export function Field({ label, hint, error, required, children, className = '' }) {
  return (
    <label className={`cc-field ${error ? 'cc-field--error' : ''} ${className}`}>
      {label && <span className="cc-field__label">{label}{required && <em aria-hidden="true"> *</em>}</span>}
      {children}
      {error ? <span className="cc-field__error"><CircleAlert size={13} />{error}</span> : hint ? <span className="cc-field__hint">{hint}</span> : null}
    </label>
  );
}

export const Select = forwardRef(function Select({ children, className = '', ...props }, ref) {
  return <span className="cc-select-wrap"><select ref={ref} className={`cc-select ${className}`} {...props}>{children}</select><ChevronDown size={15} aria-hidden="true" /></span>;
});

export function Switch({ checked, onChange, label, description, disabled }) {
  return (
    <label className={`cc-switch-row ${disabled ? 'is-disabled' : ''}`}>
      <span><strong>{label}</strong>{description && <small>{description}</small>}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange?.(e.target.checked, e)} disabled={disabled} />
      <span className="cc-switch" aria-hidden="true"><i /></span>
    </label>
  );
}

export function Tabs({ tabs, value, onChange, label = 'Sekcje' }) {
  return <div className="cc-tabs" role="tablist" aria-label={label}>{tabs.map((tab) => <button type="button" role="tab" aria-selected={value === tab.value} className={value === tab.value ? 'is-active' : ''} key={tab.value} onClick={() => onChange(tab.value)}>{tab.label}{tab.count != null && <Badge>{tab.count}</Badge>}</button>)}</div>;
}

export function Modal({ open, onOpenChange, title, description, children, footer, size = 'md', trigger }) {
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const returnFocus = document.activeElement;
    return () => window.setTimeout(() => returnFocus?.focus?.(), 0);
  }, [open]);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="cc-dialog-overlay" />
        <DialogPrimitive.Content className={`cc-dialog cc-dialog--${size}`}>
          <header><div><DialogPrimitive.Title>{title}</DialogPrimitive.Title>{description && <DialogPrimitive.Description>{description}</DialogPrimitive.Description>}</div><DialogPrimitive.Close asChild><IconButton label="Zamknij okno" icon={X} /></DialogPrimitive.Close></header>
          <div className="cc-dialog__body">{children}</div>
          {footer && <footer>{footer}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

let confirmSubscriber = null;
export const confirmAction = (options) => new Promise((resolve) => {
  if (!confirmSubscriber) { resolve(false); return; }
  confirmSubscriber({ ...(typeof options === 'string' ? { description: options } : options), resolve });
});

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  useEffect(() => { confirmSubscriber = setRequest; return () => { confirmSubscriber = null; }; }, []);
  const finish = (answer) => { request?.resolve(answer); setRequest(null); };
  return (
    <>
      {children}
      <AlertDialogPrimitive.Root open={Boolean(request)} onOpenChange={(open) => { if (!open) finish(false); }}>
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay className="cc-dialog-overlay" />
          <AlertDialogPrimitive.Content className="cc-alert-dialog">
            <div className="cc-alert-dialog__icon"><CircleAlert size={22} /></div>
            <AlertDialogPrimitive.Title>{request?.title || 'Potwierdź operację'}</AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Description>{request?.description || 'Ta operacja może wpłynąć na dane.'}</AlertDialogPrimitive.Description>
            {request?.record && <div className="cc-alert-dialog__record">{request.record}</div>}
            <div className="cc-alert-dialog__actions">
              <AlertDialogPrimitive.Cancel asChild><Button variant="secondary" onClick={() => finish(false)}>Anuluj</Button></AlertDialogPrimitive.Cancel>
              <AlertDialogPrimitive.Action asChild><Button variant={request?.destructive === false ? 'primary' : 'danger'} onClick={() => finish(true)}>{request?.confirmLabel || 'Potwierdź'}</Button></AlertDialogPrimitive.Action>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialogPrimitive.Root>
    </>
  );
}

export function DropdownMenu({ trigger, label = 'Więcej działań', items = [], align = 'end' }) {
  return (
    <DropdownPrimitive.Root>
      <Tooltip content={label}><DropdownPrimitive.Trigger asChild>{trigger || <button type="button" className="cc-icon-button" aria-label={label}><MoreHorizontal size={18} /></button>}</DropdownPrimitive.Trigger></Tooltip>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content className="cc-dropdown" align={align} sideOffset={6} collisionPadding={12}>
          {items.map((item, index) => item.separator ? <DropdownPrimitive.Separator key={`s-${index}`} /> : <DropdownPrimitive.Item key={item.id || item.label} className={item.danger ? 'is-danger' : ''} disabled={item.disabled} onSelect={item.onSelect}>{item.icon && <item.icon size={16} />}{item.label}{item.shortcut && <kbd>{item.shortcut}</kbd>}</DropdownPrimitive.Item>)}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}

const ToastContext = createContext(() => {});
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((message, tone = 'info') => { const id = Date.now() + Math.random(); setToasts((v) => [...v, { id, message, tone }]); setTimeout(() => setToasts((v) => v.filter((t) => t.id !== id)), 4200); }, []);
  return <ToastContext.Provider value={push}>{children}<div className="cc-toast-region" aria-live="polite">{toasts.map((toast) => <div key={toast.id} className={`cc-toast cc-toast--${toast.tone}`}>{toast.tone === 'success' ? <Check size={17} /> : <Info size={17} />}{toast.message}<button onClick={() => setToasts((v) => v.filter((t) => t.id !== toast.id))} aria-label="Zamknij komunikat"><X size={15} /></button></div>)}</div></ToastContext.Provider>;
}
export const useToast = () => useContext(ToastContext);

export function Skeleton({ width = '100%', height = 16, className = '' }) { return <span className={`cc-skeleton ${className}`} style={{ width, height }} aria-hidden="true" />; }

export function EmptyState({ icon: Icon = Activity, title = 'Brak danych', description = 'Nie ma jeszcze elementów do wyświetlenia.', action }) {
  return <div className="cc-empty"><span>{<Icon size={23} />}</span><h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function PageHeader({ title, description, eyebrow, breadcrumb, actions, helpId, children }) {
  return (
    <header className="cc-page-header">
      <div className="cc-page-header__copy">{eyebrow && <span>{eyebrow}</span>}{breadcrumb && <div className="cc-breadcrumb">{breadcrumb}</div>}<div className="cc-page-header__title"><h1>{title}</h1>{helpId !== false && <IconButton label="Otwórz pomoc dla tego modułu" tooltip="Jak działa ten moduł?" icon={HelpCircle} onClick={() => window.dispatchEvent(new CustomEvent('vroom:help'))} />}</div>{description && <p>{description}</p>}</div>
      {(actions || children) && <div className="cc-page-header__actions">{actions}{children}</div>}
    </header>
  );
}

export function DataTable({ records = [], columns = [], rowKey = 'id', loading, error, emptyTitle, emptyDescription, actions, onRowClick, selected = [], onSelect, pagination }) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  if (loading) return <div className="cc-table-skeleton">{[1, 2, 3, 4, 5].map((n) => <Skeleton key={n} height={48} />)}</div>;
  if (error) return <EmptyState icon={CircleAlert} title="Nie udało się pobrać danych" description={typeof error === 'string' ? error : 'Spróbuj ponownie za chwilę.'} />;
  if (!records.length) return <EmptyState title={emptyTitle} description={emptyDescription} />;
  const keyFor = (row) => typeof rowKey === 'function' ? rowKey(row) : row[rowKey];
  return (
    <div className="cc-table-wrap">
      <table className="cc-data-table">
        <thead><tr>{onSelect && <th className="cc-check-col"><input type="checkbox" aria-label="Zaznacz wszystkie" checked={records.every((r) => selectedSet.has(keyFor(r)))} onChange={(e) => onSelect(e.target.checked ? records.map(keyFor) : [])} /></th>}{columns.map((col) => <th key={col.key} className={col.className} style={{ width: col.width }}>{col.label}</th>)}{actions && <th className="cc-actions-col">Działania</th>}</tr></thead>
        <tbody>{records.map((row) => { const key = keyFor(row); const rowActions = actions?.(row) || []; return <tr key={key} onClick={() => onRowClick?.(row)} className={onRowClick ? 'is-clickable' : ''}>{onSelect && <td className="cc-check-col"><input type="checkbox" aria-label={`Zaznacz rekord ${key}`} checked={selectedSet.has(key)} onChange={(e) => { e.stopPropagation(); onSelect(e.target.checked ? [...selectedSet, key] : [...selectedSet].filter((id) => id !== key)); }} /></td>}{columns.map((col) => <td key={col.key} data-label={col.label} className={col.className}>{col.render ? col.render(row[col.key], row) : row[col.key] ?? '—'}</td>)}{actions && <td className="cc-actions-col" onClick={(e) => e.stopPropagation()}><div className="cc-row-actions">{rowActions.slice(0, 2).map((item) => <Tooltip key={item.id || item.label} content={item.label}><button type="button" className={`cc-icon-button ${item.danger ? 'is-danger' : ''}`} aria-label={item.label} onClick={item.onSelect}>{item.icon && <item.icon size={16} />}</button></Tooltip>)}{rowActions.length > 2 && <DropdownMenu items={rowActions} />}</div><div className="cc-row-actions-mobile"><DropdownMenu label="Więcej" items={rowActions} trigger={<button type="button" className="cc-button cc-button--secondary cc-button--sm">Więcej <MoreHorizontal size={16} /></button>} /></div></td>}</tr>; })}</tbody>
      </table>
      {pagination && <div className="cc-pagination"><span>{pagination.label}</span><div><Button variant="secondary" size="sm" disabled={!pagination.hasPrevious} onClick={pagination.onPrevious}>Poprzednia</Button><Button variant="secondary" size="sm" disabled={!pagination.hasNext} onClick={pagination.onNext}>Następna</Button></div></div>}
    </div>
  );
}

export function Pagination({ page = 1, total = 0, limit = 20, onPageChange, loading = false, label = 'rekordów' }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, index) => start + index);
  const rangeStart = total ? (page - 1) * limit + 1 : 0;
  const rangeEnd = Math.min(page * limit, total);
  return (
    <nav className="cc-pagination-bar" aria-label={`Paginacja ${label}`}>
      <span>Wyświetlamy <strong>{rangeStart}–{rangeEnd}</strong> z <strong>{total.toLocaleString('pl-PL')}</strong></span>
      <div>
        <IconButton label="Poprzednia strona" icon={ChevronLeft} variant="secondary" disabled={loading || page <= 1} onClick={() => onPageChange(page - 1)} />
        <div className="cc-pagination-bar__pages">{pages.map((number) => <button type="button" key={number} className={number === page ? 'is-active' : ''} aria-current={number === page ? 'page' : undefined} aria-label={`Strona ${number}`} disabled={loading} onClick={() => onPageChange(number)}>{number}</button>)}</div>
        <IconButton label="Następna strona" icon={ChevronRight} variant="secondary" disabled={loading || page >= totalPages} onClick={() => onPageChange(page + 1)} />
      </div>
      <span className="cc-pagination-bar__status">Strona {page} z {totalPages}</span>
    </nav>
  );
}
